import { Link } from "react-router-dom";
import { ArrowRight, CalendarClock, MapPinned, ShieldCheck, Radar, Route, Car, Receipt, Phone, Users, UtensilsCrossed, Wine } from "lucide-react";

const HIGHLIGHT_ICONS = { Users, UtensilsCrossed, Wine };
import { Logo } from "@/components/Logo";
import { AdSlot } from "@/components/AdSlot";
import { useAuth, homeFor } from "@/context/AuthContext";

const PHONE_DISPLAY = "214-506-2525";
const PHONE_TEL = "tel:+12145062525";

const PhoneLink = ({ testId, className = "" }) => (
  <a href={PHONE_TEL} data-testid={testId} className={`inline-flex items-center gap-2 text-amber-200 transition-colors hover:text-amber-100 ${className}`}>
    <Phone className="h-4 w-4 text-amber-400" /> <span>{PHONE_DISPLAY}</span>
  </a>
);

const FEATURES = [
  [Radar, "Nearest chauffeur, instantly", "Your request goes straight to the closest vetted driver on the road — never a queue."],
  [Route, "Up to ten stops", "Plan the whole evening. Add up to ten stops and see the route and fare before you commit."],
  [ShieldCheck, "Secure card payment", "Credit or debit card on file. Verified once, charged only when you confirm."],
  [MapPinned, "Live tracking", "Watch your car approach in real time with an ETA that updates as it moves."],
  [Receipt, "Transparent fares", "Base, distance, time and local tax — itemised on every ride, kept in your history."],
  [Car, "One signature vehicle", "Every ride is a White Nissan Armada Platinum, driven by professionals approved by our team."],
];

const Header = () => {
  const { user } = useAuth();
  return (
    <header data-testid="landing-header" className="absolute inset-x-0 top-0 z-20">
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-5 sm:h-24 sm:px-10">
        <Logo />
        <div className="flex items-center gap-3 sm:gap-6">
          <PhoneLink testId="header-phone-link" className="text-sm [&>svg]:h-4 max-[420px]:[&>span]:hidden" />
          {user ? (
            <Link to={homeFor(user.role)} data-testid="landing-portal-link" className="btn-outline-gold !px-4 !py-2.5 sm:!px-5">My portal</Link>
          ) : (
            <Link to="/login" data-testid="landing-login-link" className="btn-outline-gold !px-4 !py-2.5 sm:!px-5">Sign in</Link>
          )}
        </div>
      </div>
    </header>
  );
};

export default function Landing() {
  const { user } = useAuth();
  const bookTo = user?.role === "customer" ? "/customer" : "/signup?role=customer";
  return (
    <div className="min-h-screen bg-[#0a0a0c]" data-testid="landing-page">
      <section className="grain relative flex min-h-[100svh] items-end overflow-hidden">
        <img src="/hero.jpg" alt="White Nissan Armada Platinum on a city street at dusk" className="absolute inset-0 h-full w-full object-cover object-[80%_30%]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0c] via-[#0a0a0c]/80 to-[#0a0a0c]/10" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0c] via-transparent to-[#0a0a0c]/60" />
        <Header />
        <div className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-20 pt-36 sm:px-10 sm:pb-28">
          <p className="lux-eyebrow fade-up">Chauffeur-grade rides · Dallas</p>
          <h1 className="fade-up mt-6 max-w-3xl text-4xl font-bold leading-[1.05] tracking-tight text-amber-50 sm:text-5xl lg:text-6xl" style={{ animationDelay: "0.1s" }}>
            Arrive the way <span className="gold-text italic">you deserve!</span>
          </h1>
          <p data-testid="hero-vehicles-line" className="fade-up mt-6 inline-flex max-w-xl items-center gap-2.5 rounded-full border border-[#d4af37]/50 bg-[#d4af37]/10 px-4 py-2 text-sm font-semibold text-[#e6c687] backdrop-blur-sm sm:text-base" style={{ animationDelay: "0.15s" }}>
            <Car className="h-4 w-4 shrink-0 text-[#d4af37]" />All of our vehicles are luxury vehicles with at least 6 seats.
          </p>
          <p className="fade-up mt-6 max-w-xl text-base leading-relaxed text-zinc-300 sm:text-lg" style={{ animationDelay: "0.2s" }}>
            Luxury Rideshare pairs you with the nearest professional driver, tracks every mile live and itemises every cent — from the first stop to the tenth.
          </p>
          <div className="fade-up mt-10 flex flex-col gap-3 sm:flex-row sm:gap-4" style={{ animationDelay: "0.3s" }}>
            <Link to={bookTo} data-testid="book-ride-button" className="btn-gold !px-8 !py-4 text-base">
              Book a Ride <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/signup?role=driver" data-testid="drive-with-us-button" className="btn-outline-gold !px-8 !py-4 text-base">
              Drive with Us
            </Link>
          </div>
          <p data-testid="hero-preset-note" className="fade-up mt-6 flex max-w-xl items-start gap-2.5 rounded-2xl border-l-2 border-[#d4af37] bg-black/40 px-4 py-3 text-sm font-medium leading-snug text-[#e6c687] backdrop-blur-sm sm:text-base" style={{ animationDelay: "0.35s" }}>
            <CalendarClock className="mt-0.5 h-4 w-4 shrink-0 text-[#d4af37]" />If you pre-set your driving appointment, you will more likely be taken. You will come first!
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-5 pt-10 sm:px-10"><AdSlot placement="home_banner" /></div>

      <section className="mx-auto max-w-7xl px-5 py-24 sm:px-10 sm:py-32">
        <div className="grid gap-12 lg:grid-cols-[1fr_2fr]">
          <div>
            <p className="lux-eyebrow">The Luxury standard</p>
            <h2 className="mt-4 text-3xl text-amber-50 sm:text-4xl">Every detail, accounted for.</h2>
            <p className="mt-5 text-zinc-400">Mileage to pickup, trip mileage, fare and tax — stored on every journey for you, your driver and our team.</p>
          </div>
          <div className="grid gap-px overflow-hidden rounded-2xl border border-amber-500/10 bg-amber-500/10 sm:grid-cols-2">
            {FEATURES.map(([Icon, title, body], i) => (
              <div key={title} data-testid={`feature-${i}`} className="group bg-[#0e0e12] p-8 transition-colors duration-300 hover:bg-[#14141a]">
                <Icon className="h-6 w-6 text-amber-400 transition-transform duration-300 group-hover:-translate-y-0.5" />
                <h3 className="mt-5 text-xl text-amber-100">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-zinc-400">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 pb-24 sm:px-10 sm:pb-32" data-testid="fleet-section">
        <p className="lux-eyebrow">Our fleet</p>
        <div className="mt-4 flex flex-col justify-between gap-4 md:flex-row md:items-end">
          <h2 className="text-3xl text-amber-50 sm:text-4xl">The White Nissan Armada Platinum.</h2>
          <p className="max-w-md text-zinc-400">Full-size luxury SUV. Quilted leather captain's chairs, room for seven, and a ride as quiet as the city at midnight.</p>
        </div>
        <div className="mt-10 grid gap-4 md:grid-cols-5">
          <figure className="group relative overflow-hidden rounded-2xl border border-amber-500/15 md:col-span-3">
            <img src="/armada-34.jpg" alt="White Nissan Armada Platinum, 3/4 front view" data-testid="fleet-exterior-image" className="aspect-[3/2] h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
            <figcaption className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6 text-sm text-amber-100">Exterior · 3/4 front</figcaption>
          </figure>
          <figure className="group relative overflow-hidden rounded-2xl border border-amber-500/15 md:col-span-2">
            <img src="/armada-interior.jpg" alt="White Nissan Armada Platinum interior" data-testid="fleet-interior-image" className="aspect-[3/2] h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03] md:aspect-auto" />
            <figcaption className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6 text-sm text-amber-100">Interior · Platinum cabin</figcaption>
          </figure>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 pb-24 sm:px-10 sm:pb-32" data-testid="luxury-highlights-section">
        <div className="lux-card grain relative overflow-hidden p-8 sm:p-12">
          <p className="lux-eyebrow">The Luxury standard</p>
          <h2 className="mt-3 text-3xl text-amber-50 sm:text-4xl">Every detail, arranged for you.</h2>
          <div className="mt-10 grid gap-8 md:grid-cols-3">
            {[["Users", "All of our vehicles are luxury vehicles with at least 6 seats.", "highlight-passengers"],
              ["UtensilsCrossed", "We can provide whatever type of food or beverage you would like.", "highlight-food"],
              ["Wine", "Alcohol requests require a valid ID uploaded online with a copy of your ID.", "highlight-alcohol"]].map(([icon, text, id], i) => {
              const Icon = HIGHLIGHT_ICONS[icon];
              return (
                <div key={id} className="fade-up border-l border-amber-500/30 pl-5" style={{ animationDelay: `${i * 120}ms` }}>
                  <Icon className="h-6 w-6 text-amber-400" />
                  <p data-testid={id} className="mt-4 font-serif text-xl leading-snug text-amber-50">{text}</p>
                </div>
              );
            })}
          </div>
          <p data-testid="highlight-card-on-file" className="mt-10 border-t border-amber-500/15 pt-6 text-sm text-zinc-400">A credit card must be on file to order food or alcohol. By ordering, you authorize us to use your card to purchase these products for you.</p>
        </div>
      </section>

      <section className="border-y border-amber-500/10 bg-[#0e0e12]">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-8 px-5 py-16 sm:px-10 md:flex-row md:items-center">
          <div>
            <h2 className="text-2xl text-amber-50 sm:text-3xl">Drive with distinction.</h2>
            <p className="mt-3 max-w-lg text-zinc-400">Sign up, get approved by our team, go online and receive requests from riders closest to you.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/driver-training" data-testid="driver-training-link" className="btn-outline-gold">Driver Training</Link>
            <Link to="/signup?role=driver" data-testid="drive-with-us-secondary-button" className="btn-outline-gold">Apply to drive <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-20 sm:px-10" data-testid="contact-section">
        <div className="lux-card grid gap-8 p-8 sm:p-12 md:grid-cols-[1.4fr_1fr] md:items-center">
          <div>
            <p className="lux-eyebrow">Contact</p>
            <h2 className="mt-4 text-3xl text-amber-50 sm:text-4xl">Speak with our concierge.</h2>
            <p className="mt-4 max-w-lg text-zinc-400">Questions about a ride, corporate accounts or driving with us — our Dallas team is a call away.</p>
          </div>
          <div className="space-y-4 md:text-right">
            <a href={PHONE_TEL} data-testid="contact-phone-link" className="block font-serif text-3xl text-amber-100 transition-colors hover:text-amber-50 sm:text-4xl">{PHONE_DISPLAY}</a>
            <a href={PHONE_TEL} data-testid="contact-call-button" className="btn-gold"><Phone className="h-4 w-4" /> Call now</a>
          </div>
        </div>
      </section>

      <footer className="mx-auto flex max-w-7xl flex-col gap-6 px-5 py-12 text-sm text-zinc-500 sm:flex-row sm:items-center sm:justify-between sm:px-10">
        <Logo />
        <PhoneLink testId="footer-phone-link" />
        <nav className="flex flex-wrap gap-5">
          <Link to="/driver-training" data-testid="footer-driver-training-link" className="hover:text-amber-200">Driver Training</Link>
          <Link to="/get-the-app" data-testid="footer-get-app-link" className="hover:text-amber-200">Get the App</Link>
          <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-amber-200">Privacy</Link>
          <Link to="/terms" data-testid="footer-terms-link" className="hover:text-amber-200">Terms</Link>
          <Link to="/delete-account" data-testid="footer-delete-account-link" className="hover:text-amber-200">Delete account</Link>
        </nav>
        <p>© {new Date().getFullYear()} Luxury Rideshare · luxury-rideshare.com</p>
      </footer>
    </div>
  );
}
